import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { addYears, differenceInYears } from 'date-fns';
import type { Contract } from '../entities/Contract.js';
import type { Tarifgruppe } from '../enums/Tarifgruppe.js';

export const MAX_RECURSIVE_CALCULATION_YEARS = 100;

export function getMaxErfahrungstufe(tarifgruppe: Tarifgruppe): number {
  switch (tarifgruppe.charAt(0)) {
    case 'E':
      return 6;
    case 'A':
      return 12;
    default:
      throw new Error(`Unknown tarifgruppe: ${tarifgruppe}`);
  }
}

/**
 * Calculate Erfahrungstufe for E1. A Person in E1 starts with 2 and adds one every fourth year.
 * @param beginn
 * @returns
 */
export function calculateErfahrungsstufeE1(beginn: Date, stufe = 2, traversedYears = 0): number {
  if (traversedYears >= MAX_RECURSIVE_CALCULATION_YEARS) {
    return stufe;
  }

  const yearDiff = differenceInYears(new Date(), beginn);
  if (!Number.isFinite(yearDiff)) {
    return stufe;
  }

  if (yearDiff < 4) {
    return stufe;
  }
  const newBeginn = addYears(beginn, 4);
  return calculateErfahrungsstufeE1(newBeginn, stufe + 2, traversedYears + 4);
}

/**
 * Calculates the start of a given phase, relative to the contract start
 * @param contractStart
 * @param stufe
 * @returns the start of the phase given
 */
export function calculateStartOfErfahrungsstufe(contractStart: Date, stufe: number): Date {
  // 1 => beginn
  // 2 => beginn + 1
  // 3 => beginn + 2
  // ....
  for (let s = 1; s < stufe; s++) {
    contractStart = addYears(contractStart, s);
  }

  return contractStart;
}

export function calculateErfahrungsstufeRecursive(beginn: Date, baseTime: Date, stufe = 1, traversedYears = 0): number {
  if (traversedYears >= MAX_RECURSIVE_CALCULATION_YEARS) {
    return stufe;
  }

  const yearDiff = differenceInYears(baseTime, beginn);
  if (!Number.isFinite(yearDiff)) {
    return stufe;
  }

  if (yearDiff < 1) {
    return stufe;
  }
  if (yearDiff === 1) return stufe + 1;
  const yearsInCurrentStufe = stufe + 1;
  const newBeginn = addYears(beginn, yearsInCurrentStufe);
  return calculateErfahrungsstufeRecursive(newBeginn, baseTime, stufe + 1, traversedYears + yearsInCurrentStufe);
}

/**
 * The Erfahrungstufe is a bit more complex. It is calculated from the start of the contract or a manuel override date. An employee starts at tier 1 and remains there for 1 year. after that he goes up into tier 2 in which he remains for 2 years. 3 in 3, 4 in 4 and so on. If he hits the last tier there is no going up anymore so he remains there indefinitely.
 * @param contract the contract of the person
 * @param eingruppierung '1' | '2' for which eingruppierung the erfahrungstufe should be calculated
 * @returns {stufe: number; startOfPhase: Date} | null
 */
export function calculateErfahrungsstufe(
  contract: NarrowedEntity<
    Contract,
    {
      agInfos: { einstellungsdatum: 1 };
      agEingruppierung1: { agLohnGehaltszahlungen: { fiktivBeginnStufe1: 1; tarifgruppe: 1 } };
      agEingruppierung2: { agLohnGehaltszahlungen: { fiktivBeginnStufe1: 1; tarifgruppe: 1 } };
    }
  >,
  eingruppierung: '1' | '2',
  applyDate = new Date(),
): { stufe: number; startOfPhase: Date } | null {
  let beginn =
    eingruppierung === '1'
      ? (contract.agEingruppierung1?.agLohnGehaltszahlungen?.fiktivBeginnStufe1 ?? contract.agInfos?.einstellungsdatum)
      : (contract.agEingruppierung2?.agLohnGehaltszahlungen?.fiktivBeginnStufe1 ?? contract.agInfos?.einstellungsdatum);

  if (!beginn) return null;

  // we cannot be sure if the beginn is really a date if this function is called in a resolver
  if (!(beginn instanceof Date)) beginn = new Date(beginn);

  const tarifGruppe =
    eingruppierung === '1'
      ? contract.agEingruppierung1?.agLohnGehaltszahlungen?.tarifgruppe
      : contract.agEingruppierung2?.agLohnGehaltszahlungen?.tarifgruppe;

  if (!tarifGruppe) return null;
  const maxErfahrungsstufe = getMaxErfahrungstufe(tarifGruppe);

  const stufe = Math.min(calculateErfahrungsstufeRecursive(beginn, applyDate), maxErfahrungsstufe);
  const startOfPhase = calculateStartOfErfahrungsstufe(beginn, stufe);

  return { stufe, startOfPhase };
}
